"use client";

import { cn } from "../../../lib/cn";

export interface CtaContinuationCountdownStripProps {
	/** Elapsed fraction 0–100 (matches topic continuation timer / desktop ring). */
	continuationTimerElapsedPercent: number;
	/** When false, the strip is hidden (same visibility gate as the desktop continuation ring). */
	continuationTimerShowStrip?: boolean;
	/** When false, uses block layout for embedding in a sized clip container (e.g. blockquote footer). */
	inset?: boolean;
	className?: string;
	/** Override fill paint (default: CTA continuation strip). */
	fillClassName?: string;
}

/**
 * Bottom-edge countdown inside a {@link RippleButton}-based CTA: remaining dwell drains
 * right → left until the button becomes eligible.
 */
export function CtaContinuationCountdownStrip({
	continuationTimerElapsedPercent,
	continuationTimerShowStrip = true,
	inset = true,
	className,
	fillClassName,
}: CtaContinuationCountdownStripProps) {
	if (!continuationTimerShowStrip) {
		return null;
	}

	const elapsed = Math.max(
		0,
		Math.min(100, Number(continuationTimerElapsedPercent)),
	);
	const remainingWidthPct = 100 - elapsed;

	const fill = (
		<span
			className={cn(
				"block h-full bg-black/45 dark:bg-black/55",
				fillClassName,
			)}
			style={{ width: `${remainingWidthPct}%` }}
		/>
	);

	if (!inset) {
		return (
			<span
				aria-hidden
				className={cn(
					"pointer-events-none block h-full w-full",
					className,
				)}
			>
				{fill}
			</span>
		);
	}

	return (
		<span
			aria-hidden
			className={cn(
				"pointer-events-none absolute inset-x-0 bottom-0 z-[25] h-[3px]",
				className,
			)}
		>
			{fill}
		</span>
	);
}
